// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Icon set (D2 Evoluto)
// Porting da /design_preview/icons.jsx.
// Stile: line icons (Lucide/Tabler-like), stroke 1.75, round caps.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';

/**
 * Icona SVG generica.
 * @param {string} d          path SVG (può contenere più tag interni, viene iniettato come innerHTML del <g>)
 * @param {number} size       dimensione in px (default 20)
 * @param {string} stroke     colore stroke (default currentColor)
 * @param {number} sw         stroke width (default 1.75)
 * @param {string} fill       fill (default 'none')
 * @param {object} style      style override
 */
export const Ico = ({ d, size = 20, stroke = 'currentColor', sw = 1.75, fill = 'none', style, title }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke={stroke}
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, ...style }}
    role={title ? 'img' : 'presentation'}
    aria-label={title || undefined}
    aria-hidden={title ? undefined : true}
  >
    <g dangerouslySetInnerHTML={{ __html: d }} />
  </svg>
);

/**
 * LabThree Mark — marchio gradient (fallback quando non si usano SVG da /public/brand/).
 */
export const LabThreeMark = ({ size = 28, gradient = true }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id={`ltmk${size}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#2563EB"/>
        <stop offset="1" stopColor="#22C55E"/>
      </linearGradient>
    </defs>
    <path d="M12 2L20.66 7v10L12 22L3.34 17V7L12 2z"
      stroke={gradient ? `url(#ltmk${size})` : '#2563EB'} strokeWidth="1.8" fill="none"/>
    <path d="M7 9h7.2c1.4 0 2.3 1.4 1.7 2.6L11 17l-2.5-2.4" stroke={gradient ? `url(#ltmk${size})` : '#2563EB'}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M14.5 12.5c2.5 1.8 1 5.5-2 5.5H8" stroke={gradient ? `url(#ltmk${size})` : '#22C55E'}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

/**
 * Mappa chiave → path SVG.
 * Uso: <Ico d={I.home} size={18} />
 */
export const I = {
  // Navigation
  home:         '<path d="M3 10.5L12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1z"/>',
  list:         '<path d="M8 6h13M8 12h13M8 18h13"/><circle cx="4" cy="6" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="18" r="1"/>',
  check:        '<path d="M20 6L9 17l-5-5"/>',
  checkCircle:  '<circle cx="12" cy="12" r="10"/><path d="M8 12l3 3 5-6"/>',
  clock:        '<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>',
  history:      '<path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l3 2"/>',
  folder:       '<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>',
  chart:        '<path d="M3 3v18h18"/><path d="M7 15l4-4 3 3 5-6"/>',
  alert:        '<path d="M10.3 3.8L2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/>',
  settings:     '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .4 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.4 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.4l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .4-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.4-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.4h0a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.4l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.4 1.8v0a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/>',

  // Domain HACCP
  thermo:    '<path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/>',
  spray:     '<path d="M10 2h4v4h-4zM9 6h6l1 4H8zM9 10h6v10a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z"/><path d="M18 4h2M18 8h3M18 12h2"/>',
  sparkles:  '<path d="M11 3l1.8 4.5L17 9l-4.2 1.5L11 15l-1.8-4.5L5 9l4.2-1.5z"/><path d="M18 14l.9 2.1L21 17l-2.1.9L18 20l-.9-2.1L15 17l2.1-.9z"/><path d="M5 17l.5 1.3L7 19l-1.5.7L5 21l-.5-1.3L3 19l1.5-.7z"/>',
  wrench:    '<path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.8 2.8-2.4-2.4z"/>',
  cap:       '<path d="M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5"/>',
  chefHat:   '<path d="M6 13c-1.7 0-3-1.3-3-3 0-1.7 1.3-3 3-3 0-1.7 1.3-3 3-3 1.3 0 2.4.8 2.8 2 .4-1.2 1.5-2 2.8-2 1.7 0 3 1.3 3 3 0 0 0 0 0 0 1.7 0 3 1.3 3 3 0 1.7-1.3 3-3 3v7H6z"/><path d="M6 17h12"/>',
  package:   '<path d="M21 8L12 3 3 8v8l9 5 9-5z"/><path d="M3 8l9 5 9-5M12 13v8"/>',
  route:     '<circle cx="6" cy="19" r="3"/><circle cx="18" cy="5" r="3"/><path d="M6 16V9a6 6 0 0 1 6-6h0M18 8v7a6 6 0 0 1-6 6h0"/>',
  qr:        '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="5.5" y="5.5" width="2" height="2" fill="currentColor" stroke="none"/><rect x="16.5" y="5.5" width="2" height="2" fill="currentColor" stroke="none"/><rect x="5.5" y="16.5" width="2" height="2" fill="currentColor" stroke="none"/><path d="M14 14h2v2h-2zM18 14h3M14 18h2M17 17v4M19 18h2M14 21h2"/>',
  receipt:   '<path d="M4 2h16v20l-3-2-2 2-2-2-2 2-2-2-2 2-3-2z"/><path d="M8 7h8M8 11h8M8 15h5"/>',
  bug:       '<path d="M10 2l2 2 2-2"/><ellipse cx="12" cy="13" rx="5" ry="7"/><path d="M12 6v15"/><path d="M7 10l-4-1M17 10l4-1M7 13H3M17 13h4M7 16l-4 2M17 16l4 2"/>',
  trash:     '<path d="M3 6h18"/><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"/><path d="M5 6l1 14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-14"/><path d="M10 11v6M14 11v6"/>',
  stamp:     '<path d="M5 21h14"/><rect x="4" y="16" width="16" height="3" rx="0.5"/><path d="M8 16l1-5h6l1 5"/><rect x="10" y="6" width="4" height="5" rx="0.5"/>',
  handWash:  '<path d="M7 22v-5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v5"/><path d="M9 15V7a1.5 1.5 0 0 1 3 0v8M12 15V5a1.5 1.5 0 0 1 3 0v10M15 15V8a1.5 1.5 0 0 1 3 0v7"/><circle cx="4" cy="4" r="1.5"/><circle cx="6" cy="8" r="0.8"/>',

  // Profilo / azienda / ruoli
  pin:       '<circle cx="12" cy="10" r="3"/><path d="M12 22s7-7.6 7-12a7 7 0 1 0-14 0c0 4.4 7 12 7 12z"/>',
  user:      '<circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/>',
  users:     '<circle cx="9" cy="8" r="4"/><path d="M2 21a7 7 0 0 1 14 0"/><path d="M16 4a4 4 0 0 1 0 8"/><path d="M22 21a7 7 0 0 0-5-6.7"/>',
  building:  '<path d="M3 21h18M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/><path d="M9 7h.01M9 11h.01M9 15h.01M14 7h.01M14 11h.01M14 15h.01"/>',
  calendar:  '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/>',

  // Comandi / azioni
  bell:       '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10 21a2 2 0 0 0 4 0"/>',
  mail:       '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/>',
  phone:      '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>',
  search:     '<circle cx="11" cy="11" r="7"/><path d="M20 20l-4-4"/>',
  plus:       '<path d="M12 5v14M5 12h14"/>',
  arrowRight: '<path d="M5 12h14M13 5l7 7-7 7"/>',
  arrowLeft:  '<path d="M19 12H5M11 19l-7-7 7-7"/>',
  chevronR:   '<path d="M9 6l6 6-6 6"/>',
  chevronD:   '<path d="M6 9l6 6 6-6"/>',
  x:          '<path d="M18 6L6 18M6 6l12 12"/>',
  dot:        '<circle cx="12" cy="12" r="4" fill="currentColor"/>',
  flame:      '<path d="M12 2S4 10 4 15a8 8 0 0 0 16 0c0-2-1-4-3-5 1 4-3 5-3 5s1-5-2-13z"/>',
  trend:      '<path d="M3 17l6-6 4 4 8-8"/><path d="M14 7h7v7"/>',
  filter:     '<path d="M3 4h18l-7 9v6l-4 2v-8z"/>',
  download:   '<path d="M12 3v12M7 10l5 5 5-5M5 21h14"/>',
  eye:        '<path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>',
  signature:  '<path d="M3 17s3-1 5-4 3-8 3-8-2 6-1 9 3 4 5 2"/><path d="M13 14s2 4 5 3 3-4 3-4"/>',
  camera:     '<path d="M3 7h4l2-3h6l2 3h4v13H3z"/><circle cx="12" cy="13" r="4"/>',
  edit:       '<path d="M11 4H4v16h16v-7"/><path d="M18 2l4 4-12 12H6v-4z"/>',
  grid:       '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',
  layers:     '<path d="M12 2L2 8l10 6 10-6z"/><path d="M2 14l10 6 10-6M2 11l10 6 10-6"/>',
  shield:     '<path d="M12 2l9 4v6c0 5-4 9-9 10-5-1-9-5-9-10V6z"/><path d="M8 12l3 3 5-6"/>',
  play:       '<path d="M6 4l14 8-14 8z" fill="currentColor" stroke="none"/>',
  logout:     '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>',
  drop:       '<path d="M12 2s7 7 7 13a7 7 0 0 1-14 0c0-6 7-13 7-13z"/>',
  book:       '<path d="M4 4h6a3 3 0 0 1 3 3v14a2 2 0 0 0-2-2H4z"/><path d="M20 4h-6a3 3 0 0 0-3 3v14a2 2 0 0 1 2-2h7z"/>',
  lock:       '<rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>',
  repeat:     '<path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>',
  arrowDown:  '<path d="M12 5v14M19 12l-7 7-7-7"/>',
  skip:       '<path d="M5 4v16l10-8z" fill="currentColor" stroke="none"/><path d="M19 4v16"/>',
  info:       '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>',
  sparkle:    '<path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z"/>',
  target:     '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>',

  // Tracciabilità
  barcode:   '<path d="M3 5v14M6 5v14M9 5v14M12 5v14M15 5v14M18 5v14M21 5v14" stroke-width="1.5"/>',
  truck:     '<rect x="1" y="7" width="14" height="10" rx="1"/><path d="M15 10h4l3 4v3h-7z"/><circle cx="6" cy="18" r="2"/><circle cx="18" cy="18" r="2"/>',
  factory:   '<path d="M3 21V10l6 4V10l6 4V7l6 4v10z"/><path d="M7 17h2M11 17h2M15 17h2"/>',
  flask:     '<path d="M9 2h6"/><path d="M10 2v7l-6 11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1l-6-11V2"/>',
  pkg:       '<path d="M21 8l-9 5-9-5 9-5z"/><path d="M3 8v8l9 5 9-5V8"/>',
  gitFork:   '<circle cx="6" cy="6" r="2"/><circle cx="18" cy="6" r="2"/><circle cx="12" cy="18" r="2"/><path d="M6 8v2a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8M12 12v4"/>',
  branch:    '<path d="M4 3v12a3 3 0 0 0 3 3h6"/><path d="M20 9v0a3 3 0 0 1-3 3h-4M20 9a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM4 3a2 2 0 1 0-4 0 2 2 0 0 0 4 0zM13 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"/>',
};

export default I;
